import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { fetchClient } from '@/lib/openapi-fetch/fetchClient';
import {
  isManagementLevel,
  type JobSignal,
  type TitleVerdict,
} from '@/modules/jobs/signal/types';
import {
  toCompletenessLabel,
  toNextStep,
  toSufficiencyLevel,
} from '@/modules/jobs/signal/verdict';
import type { ApiRequestBody } from '@/types/utils/request';

type JobSignalRequestBody = ApiRequestBody<'/ai/job-signal', 'post'>;

export interface JobSignalInputs extends Omit<
  JobSignalRequestBody,
  'seniority' | 'titleVerdict'
> {
  titleVerdict: TitleVerdict | null;
  seniority: string[];
  /** the verdict of each committed must-have skill (from `/ai/skill-lint`) */
  skillVerdicts: string[];
}

/** A safe default before the first result lands, or on an unexpected empty response. */
const EMPTY_JOB_SIGNAL: JobSignal = {
  completeness: { strength: 0, label: 'thin', nextStep: null },
  skillSufficiency: { level: 'none' },
  placeholder: true,
};

/**
 * Brief-completeness + skill-sufficiency signal from `/ai/job-signal` — a deterministic backend heuristic
 * (PLACEHOLDER; see the plan §9.4). The wire's bare-string `label`/`nextStep`/`level` are narrowed to local unions.
 *
 * The query key is the set of inputs the heuristic actually depends on — title/city **presence** (not their text),
 * the title verdict, seniority presence, and the skill verdicts — so typing *within* a field doesn't trigger a
 * refetch (no per-keystroke chatter, no debounce needed). The queryFn still sends the raw draft; the backend reads
 * only presence + verdicts today. Revisit this key if the data-grounded scorer starts using the raw title/city text.
 */
export const useJobSignalQuery = (
  inputs: JobSignalInputs,
  initialData?: JobSignal,
) =>
  useQuery({
    queryKey: [
      'ai',
      'job-signal',
      inputs.title.trim().length > 0,
      inputs.titleVerdict,
      inputs.city.trim().length > 0,
      inputs.seniority.length > 0,
      inputs.skillVerdicts,
    ] as const,
    queryFn: async (): Promise<JobSignal> => {
      const { data } = await fetchClient.POST('/ai/job-signal', {
        body: {
          title: inputs.title,
          titleVerdict: inputs.titleVerdict,
          city: inputs.city,
          // draft holds string[] (the MultiSelect is generically string-typed); narrow to the wire enum so a
          // stale/renamed level (e.g. from a future restored draft) is dropped rather than rejected with a 400.
          seniority: inputs.seniority.filter(isManagementLevel),
          skillVerdicts: inputs.skillVerdicts,
        },
      });
      // Defensive: a 200 with a missing/partial body falls back rather than throwing on a nested access. (A real
      // 4xx/5xx never reaches here — errorMiddleware throws, so the query goes to its error state.)
      if (!data?.completeness || !data.skillSufficiency)
        return EMPTY_JOB_SIGNAL;
      return {
        completeness: {
          strength: data.completeness.strength,
          label: toCompletenessLabel(data.completeness.label),
          nextStep: toNextStep(data.completeness.nextStep),
        },
        skillSufficiency: {
          level: toSufficiencyLevel(data.skillSufficiency.level),
        },
        placeholder: data.placeholder,
      };
    },
    placeholderData: keepPreviousData,
    initialData,
    // Deterministic heuristic — a given draft always scores the same, so cache aggressively.
    staleTime: 5 * 60_000,
  });
